import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, ChevronDown, ChevronUp, Pencil, Trash2, Search, ShieldOff, MapPin, MapPinOff, MapPinned, X, FolderOpen, Check, Plus, Settings, RefreshCw } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

function floorLabel(floor: number): string {
  return floor === -1 ? 'B1' : `${floor}F`;
}

function unitDisplayCode(floor: number, unitNumber: string): string {
  const num = Number(unitNumber);
  if (floor === -1) return !isNaN(num) ? `B${100 + num}` : `B${unitNumber}`;
  if (!isNaN(num) && num >= 1 && num <= 10) return String(floor * 100 + num);
  return `${floor}F-${unitNumber}`;
}

type FieldSurveyUnit = { id: number; floor: number; unitNumber: string };
type FieldSurveyGroup = {
  id: number;
  name: string;
  parentId: number | null;
  polygon: string | null;
  _count: { surveys: number; children: number };
  children?: FieldSurveyGroup[];
};
type FieldSurvey = {
  id: number;
  groupId: number | null;
  group: { id: number; name: string } | null;
  address: string;
  dongName: string | null;
  memo: string | null;
  isAccessRestricted: boolean;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  updatedAt: string;
  units: FieldSurveyUnit[];
  _count: { units: number };
};

const FieldSurveyHistoryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterGroupId, setFilterGroupId] = useState<string>('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [geocodeFailedAddresses, setGeocodeFailedAddresses] = useState<string[]>([]);
  const [mapModalSurvey, setMapModalSurvey] = useState<FieldSurvey | null>(null);
  const [bulkPolygonProgress, setBulkPolygonProgress] = useState<string | null>(null);

  // Fetch groups
  const { data: groupsData } = useQuery({
    queryKey: ['field-survey-groups'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
    refetchInterval: 15000,
  });
  const groups: FieldSurveyGroup[] = groupsData?.data || [];

  // Fetch all surveys (for noCoordsCount)
  const { data: allSurveysData } = useQuery({
    queryKey: ['field-surveys'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
    refetchInterval: 15000,
  });
  const allSurveys: FieldSurvey[] = allSurveysData?.data || [];
  const noCoordsCount = allSurveys.filter(s => s.latitude === null).length;

  // Fetch surveys (filtered)
  const { data, isLoading } = useQuery({
    queryKey: ['field-surveys-history', searchQuery, filterGroupId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (filterGroupId === 'noCoords') {
        params.set('noCoords', 'true');
      } else if (filterGroupId) {
        params.set('groupId', filterGroupId);
      }
      const qs = params.toString();
      const res = await fetch(`${API_BASE}/api/field-surveys${qs ? `?${qs}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
    refetchInterval: 15000,
  });

  const { data: detailData } = useQuery({
    queryKey: ['field-survey-detail', expandedId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${expandedId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
    enabled: expandedId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('삭제에 실패했습니다');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      toast.success('삭제되었습니다');
      setExpandedId(null);
    },
  });

  const geocodeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/field-surveys/geocode-all`, { method: 'POST' });
      if (!res.ok) throw new Error('실패했습니다');
      return res.json();
    },
    onSuccess: (data: { message: string; failedAddresses: string[] }) => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys'] });
      toast.success(data.message);
      if (data.failedAddresses?.length > 0) {
        setGeocodeFailedAddresses(data.failedAddresses);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkGroupMutation = useMutation({
    mutationFn: async ({ surveyIds, groupId }: { surveyIds: number[]; groupId: number | null }) => {
      const res = await fetch(`${API_BASE}/api/field-surveys/bulk-group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surveyIds, groupId }),
      });
      if (!res.ok) throw new Error('실패했습니다');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      toast.success('그룹이 변경되었습니다');
      setSelectMode(false);
      setSelectedIds(new Set());
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || '실패'); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      toast.success('그룹이 추가되었습니다');
      setNewGroupName('');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error('실패');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      queryClient.invalidateQueries({ queryKey: ['field-surveys-history'] });
      toast.success('그룹명이 수정되었습니다');
      setEditingGroupId(null);
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/api/field-survey-groups/${id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || '삭제 실패'); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
      toast.success('그룹이 삭제되었습니다');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const surveys: FieldSurvey[] = data?.data || [];
  const detail: FieldSurvey | null = detailData?.data || null;

  const getGroupedUnits = (units: FieldSurveyUnit[]) => {
    const grouped: Record<number, string[]> = {};
    units.forEach(u => { if (!grouped[u.floor]) grouped[u.floor] = []; grouped[u.floor]!.push(u.unitNumber); });
    return [5, 4, 3, 2, 1, -1].filter(f => grouped[f] && grouped[f].length > 0)
      .map(f => ({ floor: f, units: grouped[f]!.sort((a, b) => { const nA = Number(a), nB = Number(b); if (!isNaN(nA) && !isNaN(nB)) return nA - nB; return a.localeCompare(b); }) }));
  };

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setSearchQuery(searchInput.trim()); setExpandedId(null); };
  const handleExport = () => window.open(`${API_BASE}/api/field-surveys/export`, '_blank');

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const handleBulkGroup = (groupId: number | null) => {
    bulkGroupMutation.mutate({ surveyIds: Array.from(selectedIds), groupId });
  };

  if (isLoading) {
    return <div className="page-narrow"><p className="text-gray-400 py-12 text-center">로딩 중...</p></div>;
  }

  return (
    <div className="page-narrow">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="page-title">조사 이력</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowGroupManager(!showGroupManager)}
            className="btn-sm btn-secondary flex items-center gap-1">
            <Settings size={14} />
          </button>
          <button onClick={() => { if (confirm('좌표가 없는 조사 데이터의 좌표를 생성합니다.')) geocodeMutation.mutate(); }}
            disabled={geocodeMutation.isPending || noCoordsCount === 0}
            className="btn-sm btn-secondary flex items-center gap-1.5">
            <RefreshCw size={14} className={geocodeMutation.isPending ? 'animate-spin' : ''} />
            {geocodeMutation.isPending ? '처리중' : '좌표생성'}
            {noCoordsCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {noCoordsCount}
              </span>
            )}
          </button>
          <button onClick={() => navigate('/field-survey/map')}
            className="btn-sm btn-secondary flex items-center gap-1.5">
            <MapPin size={14} /> 지도
          </button>
          <button onClick={handleExport} disabled={surveys.length === 0}
            className="btn-sm btn-success flex items-center gap-1.5">
            <Download size={14} /> 엑셀
          </button>
        </div>
      </div>

      {/* Group Manager */}
      {showGroupManager && (
        <div className="card card-body mb-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">그룹 관리</h3>
          <div className="space-y-2 mb-3">
            {groups.filter(g => g.parentId === null).map(g => (
              <div key={g.id}>
                <div className="flex items-center gap-2">
                  {editingGroupId === g.id ? (
                    <>
                      <input value={editingGroupName} onChange={e => setEditingGroupName(e.target.value)}
                        className="input-field h-8 text-sm flex-1" autoFocus />
                      <button onClick={() => updateGroupMutation.mutate({ id: g.id, name: editingGroupName })}
                        className="text-brand-600 text-xs font-medium">저장</button>
                      <button onClick={() => setEditingGroupId(null)} className="text-gray-400 text-xs">취소</button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-gray-700 flex-1">{g.name}</span>
                      <span className="text-xs text-gray-400">{g._count.surveys}건</span>
                      {g.children && g.children.length > 0 && (
                        <button
                          disabled={!!bulkPolygonProgress}
                          onClick={async () => {
                            const subs = g.children!.filter(s => s._count.surveys > 0);
                            if (subs.length === 0) return;
                            for (let i = 0; i < subs.length; i++) {
                              setBulkPolygonProgress(`영역 갱신 중 (${i + 1}/${subs.length})`);
                              try {
                                await fetch(`${API_BASE}/api/field-survey-groups/${subs[i]!.id}/generate-polygon`, { method: 'POST' });
                              } catch { /* continue */ }
                            }
                            setBulkPolygonProgress(null);
                            queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
                            toast.success(`${subs.length}개 서브 그룹 영역이 갱신되었습니다`);
                          }}
                          className="text-xs text-brand-600">
                          {bulkPolygonProgress || '일괄 영역갱신'}
                        </button>
                      )}
                      <button onClick={() => { setEditingGroupId(g.id); setEditingGroupName(g.name); }}
                        className="text-xs text-brand-600">수정</button>
                      <button onClick={() => { if (confirm(`"${g.name}" 그룹을 삭제하시겠습니까?`)) deleteGroupMutation.mutate(g.id); }}
                        className="text-xs text-red-500">삭제</button>
                    </>
                  )}
                </div>
                {/* 서브 그룹 */}
                {g.children && g.children.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {g.children.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400">└</span>
                        <span className="text-gray-600 flex-1">{sub.name}</span>
                        <span className="text-gray-400">{sub._count.surveys}건</span>
                        {sub._count.surveys > 0 && (
                          <button onClick={async () => {
                            try {
                              const res = await fetch(`${API_BASE}/api/field-survey-groups/${sub.id}/generate-polygon`, { method: 'POST' });
                              if (!res.ok) { const e = await res.json(); toast.error(e.error || 'Polygon 생성 실패'); return; }
                              queryClient.invalidateQueries({ queryKey: ['field-survey-groups'] });
                              toast.success('Polygon이 생성되었습니다');
                            } catch { toast.error('Polygon 생성 실패'); }
                          }}
                            className="text-xs text-brand-600">
                            {sub.polygon ? '영역갱신' : '영역생성'}
                          </button>
                        )}
                        {sub.polygon && (
                          <button onClick={() => {
                            navigator.clipboard.writeText(sub.polygon!);
                            toast.success('Polygon JSON이 복사되었습니다');
                          }}
                            className="text-xs text-gray-500">JSON</button>
                        )}
                        {sub._count.surveys > 0 && (
                          <button onClick={() => window.open(`${API_BASE}/api/field-surveys/export-territory?groupId=${sub.id}`, '_blank')}
                            className="text-xs text-emerald-600">
                            <Download size={12} className="inline mr-0.5" />구역카드
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={e => { e.preventDefault(); if (newGroupName.trim()) createGroupMutation.mutate(newGroupName.trim()); }}
            className="flex gap-2">
            <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
              placeholder="새 그룹 이름" className="input-field h-8 text-sm flex-1" />
            <button type="submit" disabled={!newGroupName.trim()}
              className="btn-sm btn-primary flex items-center gap-1">
              <Plus size={14} /> 추가
            </button>
          </form>
        </div>
      )}

      {/* 좌표 생성 실패 목록 */}
      {geocodeFailedAddresses.length > 0 && (
        <div className="card card-body mb-4 border-red-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-red-600">좌표 생성 실패 ({geocodeFailedAddresses.length}건)</h3>
            <button onClick={() => setGeocodeFailedAddresses([])}
              className="text-gray-300 active:text-gray-500">
              <X size={16} />
            </button>
          </div>
          <ul className="space-y-1">
            {geocodeFailedAddresses.map((addr, i) => (
              <li key={i} className="text-xs text-gray-600">{addr}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-3">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
          <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="주소, 동, 메모, 그룹 검색..." className="input-field pl-10 h-10 text-sm" />
          {searchQuery && (
            <button type="button" onClick={() => { setSearchInput(''); setSearchQuery(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 active:text-gray-500">
              <X size={16} />
            </button>
          )}
        </div>
      </form>

      {/* Group filter tabs */}
      <div className="flex gap-1.5 overflow-x-auto mb-4 pb-1 scrollbar-hide">
        <button onClick={() => setFilterGroupId('')}
          className={`btn-sm whitespace-nowrap ${!filterGroupId ? 'btn-primary' : 'btn-secondary'}`}>
          전체
        </button>
        <button onClick={() => setFilterGroupId('null')}
          className={`btn-sm whitespace-nowrap ${filterGroupId === 'null' ? 'btn-primary' : 'btn-secondary'}`}>
          미분류
        </button>
        {noCoordsCount > 0 && (
          <button onClick={() => setFilterGroupId('noCoords')}
            className={`btn-sm whitespace-nowrap ${filterGroupId === 'noCoords' ? 'bg-orange-500 text-white' : 'btn-secondary text-orange-500'}`}>
            좌표 없음 ({noCoordsCount})
          </button>
        )}
        {groups.filter(g => g.parentId === null).map(g => {
          const childIds = (g.children || []).map(c => String(c.id));
          const isActive = filterGroupId === String(g.id) || childIds.includes(filterGroupId);
          const totalSurveys = g._count.surveys + (g.children || []).reduce((s, c) => s + c._count.surveys, 0);
          return (
            <button key={g.id} onClick={() => setFilterGroupId(String(g.id))}
              className={`btn-sm whitespace-nowrap ${isActive ? 'btn-primary' : 'btn-secondary'}`}>
              {g.name} ({totalSurveys})
            </button>
          );
        })}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="card card-body text-center">
          <p className="text-2xl font-bold text-brand-600">{surveys.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">조사 건수</p>
        </div>
        <div className="card card-body text-center">
          <p className="text-2xl font-bold text-brand-600">
            {surveys.reduce((sum, s) => sum + s._count.units, 0)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">총 호수</p>
        </div>
      </div>

      {/* Select mode toggle */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
            selectMode ? 'bg-brand-50 text-brand-700' : 'text-gray-400 active:bg-gray-100'
          }`}>
          <FolderOpen size={14} className="inline mr-1" />
          {selectMode ? '선택 취소' : '그룹 할당'}
        </button>
        {selectMode && selectedIds.size > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">{selectedIds.size}건 선택</span>
            <select
              onChange={e => {
                const val = e.target.value;
                if (val === '') return;
                handleBulkGroup(val === 'null' ? null : parseInt(val));
                e.target.value = '';
              }}
              className="text-xs border border-gray-200 rounded-lg h-8 px-2"
              defaultValue=""
            >
              <option value="" disabled>그룹 이동</option>
              <option value="null">미분류</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* List */}
      {surveys.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-300 text-sm">
            {searchQuery ? `"${searchQuery}" 검색 결과 없음` : '조사 이력이 없습니다'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {surveys.map(survey => {
            const isExpanded = expandedId === survey.id && !selectMode;
            const isSelected = selectedIds.has(survey.id);
            const grouped = isExpanded && detail && detail.id === survey.id
              ? getGroupedUnits(detail.units) : [];

            return (
              <div key={survey.id} className={`card overflow-hidden ${isSelected ? 'ring-2 ring-brand-500' : ''}`}>
                <button
                  onClick={() => {
                    if (selectMode) toggleSelect(survey.id);
                    else setExpandedId(isExpanded ? null : survey.id);
                  }}
                  className="w-full text-left card-body active:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {selectMode && (
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-brand-500 border-brand-500' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{survey.address}</p>
                        {survey.latitude === null && <MapPinOff size={14} className="text-orange-400 flex-shrink-0" />}
                        {survey.isAccessRestricted && <ShieldOff size={14} className="text-red-400 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        <select
                          value={survey.groupId ?? ''}
                          onChange={e => {
                            e.stopPropagation();
                            const val = e.target.value;
                            bulkGroupMutation.mutate({
                              surveyIds: [survey.id],
                              groupId: val === '' ? null : parseInt(val),
                            });
                          }}
                          onClick={e => e.stopPropagation()}
                          className={`text-[11px] font-medium rounded px-1.5 py-0.5 mr-1.5 border-0 appearance-none cursor-pointer ${
                            survey.group ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          <option value="">미분류</option>
                          {groups.filter(g => g.parentId === null).map(g => (
                            <optgroup key={g.id} label={g.name}>
                              <option value={g.id}>{g.name}</option>
                              {g.children?.map(sub => (
                                <option key={sub.id} value={sub.id}>└ {sub.name}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <span className="badge-blue mr-1.5">{survey._count.units}호</span>
                        {survey.dongName && `${survey.dongName} · `}
                        {new Date(survey.createdAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {survey.memo && <span className="text-gray-300"> · {survey.memo}</span>}
                      </p>
                    </div>
                    {!selectMode && (
                      isExpanded ? <ChevronUp size={18} className="text-gray-300 ml-2" /> : <ChevronDown size={18} className="text-gray-300 ml-2" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-50 px-4 pb-4">
                    {detail && detail.id === survey.id ? (
                      <>
                        <div className="py-3 space-y-2">
                          {grouped.map(({ floor, units }) => (
                            <div key={floor} className="flex items-start">
                              <span className="text-xs font-bold text-gray-300 w-7 flex-shrink-0 pt-0.5">{floorLabel(floor)}</span>
                              <div className="flex flex-wrap gap-1 ml-2">
                                {units.map(u => <span key={u} className="badge-blue">{unitDisplayCode(floor, u)}</span>)}
                              </div>
                            </div>
                          ))}
                        </div>
                        {survey.updatedAt && survey.updatedAt !== survey.createdAt && (
                          <p className="text-xs text-gray-300 pt-2">
                            수정: {new Date(survey.updatedAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                        <div className="flex gap-3 pt-1">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/field-survey/${survey.id}`); }}
                            className="flex items-center gap-1 text-xs text-brand-600 font-medium">
                            <Pencil size={13} /> 편집
                          </button>
                          {survey.latitude !== null && (
                            <button onClick={(e) => { e.stopPropagation(); setMapModalSurvey(survey); }}
                              className="flex items-center gap-1 text-xs text-brand-600 font-medium">
                              <MapPin size={13} /> 지도보기
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/field-survey/map?pin=${survey.id}`); }}
                            className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                            <MapPinned size={13} /> {survey.latitude !== null ? '핀 수정' : '핀 찍기'}
                          </button>
                          <button onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('삭제하시겠습니까?')) deleteMutation.mutate(survey.id);
                          }} className="flex items-center gap-1 btn-danger-text">
                            <Trash2 size={13} /> 삭제
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="py-3 text-xs text-gray-300">로딩 중...</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* 지도 모달 */}
      {mapModalSurvey && mapModalSurvey.latitude !== null && (
        <MapModal survey={mapModalSurvey} allSurveys={allSurveys} onClose={() => setMapModalSurvey(null)} />
      )}
    </div>
  );
};

// 인라인 지도 모달 컴포넌트
const loadKakaoMapsSdk = async (): Promise<void> => {
  const apiKey = import.meta.env.VITE_KAKAO_APPKEY;
  if (!apiKey) throw new Error('카카오맵 API 키가 설정되지 않았습니다.');
  if ((window as any).kakao?.maps) return; // eslint-disable-line @typescript-eslint/no-explicit-any
  const existingScript = document.querySelector<HTMLScriptElement>('script[data-kakao-map-sdk="true"]');
  if (existingScript) {
    await new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('SDK 로드 실패')), { once: true });
    });
    return;
  }
  const script = document.createElement('script');
  script.setAttribute('data-kakao-map-sdk', 'true');
  script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false`;
  script.async = true;
  const promise = new Promise<void>((resolve, reject) => {
    script.onload = () => { (window as any).kakao.maps.load(() => resolve()); }; // eslint-disable-line @typescript-eslint/no-explicit-any
    script.onerror = () => reject(new Error('SDK 로드 실패'));
  });
  document.head.appendChild(script);
  await promise;
};

const MapModal: React.FC<{ survey: FieldSurvey; allSurveys: FieldSurvey[]; onClose: () => void }> = ({ survey, allSurveys, onClose }) => {
  const mapRef = useRef<HTMLDivElement>(null);

  // 같은 좌표의 조사 건수
  const sameLocationSurveys = allSurveys.filter(
    s => s.latitude === survey.latitude && s.longitude === survey.longitude
  );
  const overlapCount = sameLocationSurveys.length;

  useEffect(() => {
    if (!mapRef.current || !survey.latitude || !survey.longitude) return;
    let isMounted = true;
    const init = async () => {
      try {
        await loadKakaoMapsSdk();
        if (!isMounted || !mapRef.current) return;
        const kakao = (window as any).kakao; // eslint-disable-line @typescript-eslint/no-explicit-any
        const position = new kakao.maps.LatLng(survey.latitude, survey.longitude);
        const map = new kakao.maps.Map(mapRef.current, { center: position, level: 3 });
        const totalUnits = sameLocationSurveys.reduce((sum, s) => sum + s._count.units, 0);
        const hasRestricted = sameLocationSurveys.some(s => s.isAccessRestricted);
        const markerColor = hasRestricted ? '#ef4444' : '#3b82f6';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
          <path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.3 21.7 0 14 0z" fill="${markerColor}"/>
          <circle cx="14" cy="14" r="6" fill="white"/>
          <text x="14" y="17" text-anchor="middle" font-size="9" font-weight="bold" fill="${markerColor}">${totalUnits}</text>
        </svg>`;
        const markerImage = new kakao.maps.MarkerImage(
          `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
          new kakao.maps.Size(28, 36),
          { offset: new kakao.maps.Point(14, 36) }
        );
        new kakao.maps.Marker({ map, position, image: markerImage });
      } catch { /* ignore */ }
    };
    init();
    return () => { isMounted = false; };
  }, [survey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-2xl mx-4 w-full max-w-lg shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">{survey.address}</p>
            <p className="text-xs text-gray-400">
              {survey.dongName && `${survey.dongName} · `}{survey._count.units}호
              {overlapCount > 1 && <span className="text-amber-500 ml-1">· 이 위치 {overlapCount}건</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-300 active:text-gray-500">
            <X size={18} />
          </button>
        </div>
        <div ref={mapRef} style={{ height: 300 }} />
      </div>
    </div>
  );
};

export default FieldSurveyHistoryPage;
